import { useSeo } from "../lib/seo";
import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { BoaCard } from "../components/BoaCard";
import { ChevronDown, Check } from "lucide-react";

export default function Shop() {
  useSeo({
    title: "The Collection | Quantum Boas",
    description: "Browse the Quantum Boas collection — high-end boa constrictor morphs with documented genetics and parentage. Filter by trait, sex, price and availability.",
  });
  const [boas, setBoas] = useState([]);
  const [morphsList, setMorphsList] = useState([]);
  const [filter, setFilter] = useState("available");
  const [selected, setSelected] = useState([]);
  const [sort, setSort] = useState("newest");
  const [open, setOpen] = useState(false);
  const ddRef = useRef(null);

  useEffect(() => {
    api.get("/boas").then((r) => setBoas(r.data)).catch(() => {});
    api.get("/morphs").then((r) => setMorphsList(r.data.map((m) => m.name).sort())).catch(() => {});
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ddRef.current && !ddRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggleTrait = (t) => setSelected((s) => (s.includes(t) ? s.filter((x) => x !== t) : [...s, t]));

  const shown = boas.filter(
    (b) => b.status !== "sold" && b.status !== "nfs" && (filter === "all" || b.status === filter) && (selected.length === 0 || selected.some((t) => (b.morphs || []).includes(t)))
  );
  const yr = (b) => parseInt(b.year, 10) || 0;
  const sorted = [...shown].sort((a, b) => {
    if (sort === "price-asc") return (a.price || 0) - (b.price || 0);
    if (sort === "price-desc") return (b.price || 0) - (a.price || 0);
    if (sort === "name") return a.name.localeCompare(b.name);
    if (sort === "age-old") return yr(a) - yr(b);
    if (sort === "age-young") return yr(b) - yr(a);
    return 0;
  });

  return (
    <div className="relative min-h-screen pt-32 pb-28 px-6 md:px-10 bg-[#F4F1E8] text-[#0e0e0e]">
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 -right-24 w-[520px] h-[520px] rounded-full bg-qbsecondary/40 blur-[130px] hidden sm:block" />
        <div className="absolute top-1/4 -left-40 w-[460px] h-[460px] rounded-full bg-qbprimary/40 blur-[130px] hidden sm:block" />
      </div>

      <div className="relative max-w-[1400px] mx-auto">
        <span className="text-xs tracking-[0.28em] uppercase text-qbsecondary font-semibold">The collection</span>
        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl mt-4 mb-4 leading-[1.02] pb-1">
          Available <span className="italic watercolor-text">boas</span>
        </h1>
        <p className="text-black/60 max-w-xl mb-8 text-base">
          Every animal is documented with full genetics and parentage. Filter by availability or traits to find your next project.
        </p>

        {/* Filter / sort bar — sticky so it stays visible while scrolling */}
        <div className="sticky top-20 z-30 -mx-6 md:-mx-10 px-6 md:px-10 py-3 mb-6 bg-[#F4F1E8]/95 backdrop-blur border-y border-black/10 flex items-center gap-3 flex-wrap" data-testid="sort-bar">
          <div className="flex items-center gap-3 flex-wrap">
            <select data-testid="availability-select" value={filter} onChange={(e) => setFilter(e.target.value)}
              className="bg-white/70 text-black border border-black/15 rounded-full px-4 py-2 text-sm outline-none focus:border-black/60 capitalize">
              <option value="available">Available</option>
              <option value="all">All</option>
              <option value="reserved">Reserved</option>
            </select>
            <div className="relative" ref={ddRef}>
              <button data-testid="traits-filter-btn" onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-2 bg-white/70 text-black border border-black/15 rounded-full px-4 py-2 text-sm hover:border-black/50">
                Traits{selected.length ? ` (${selected.length})` : ""} <ChevronDown className="w-4 h-4" />
              </button>
              {open && (
                <div data-testid="traits-panel" className="absolute left-0 mt-2 w-64 max-h-72 overflow-y-auto bg-white text-black rounded-xl border border-black/10 shadow-xl z-40 p-2">
                  <div className="flex items-center justify-between px-2 py-1">
                    <span className="text-xs uppercase tracking-wider text-black/40">Genetics</span>
                    {selected.length > 0 && <button onClick={() => setSelected([])} className="text-xs text-qbsecondary">Clear</button>}
                  </div>
                  {morphsList.map((m) => (
                    <button key={m} data-testid={`trait-opt-${m}`} onClick={() => toggleTrait(m)}
                      className="w-full flex items-center justify-between px-2 py-1.5 rounded-md text-sm hover:bg-black/5">
                      <span>{m}</span>
                      {selected.includes(m) && <Check className="w-4 h-4 text-emerald-600" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="hidden md:inline text-xs uppercase tracking-wider text-black/50">Sort</span>
              <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-white/70 text-black border border-black/15 rounded-full px-4 py-2 text-sm outline-none focus:border-black/60">
                <option value="newest">Newest</option>
                <option value="price-asc">Price: Low to High</option>
                <option value="price-desc">Price: High to Low</option>
                <option value="age-old">Age: Oldest to Youngest</option>
                <option value="age-young">Age: Youngest to Oldest</option>
                <option value="name">Name A–Z</option>
              </select>
            </div>
          </div>
        </div>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6" data-testid="selected-traits">
            {selected.map((t) => (
              <button key={t} onClick={() => toggleTrait(t)} className="text-xs px-3 py-1.5 rounded-full bg-qbprimary/15 border border-qbprimary/40 text-emerald-800">
                {t} ✕
              </button>
            ))}
          </div>
        )}

        {sorted.length === 0 ? (
          <p className="text-black/40 py-20 text-center">No animals match these filters.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2 sm:gap-4 lg:gap-5" data-testid="shop-grid">
            {sorted.map((b) => <BoaCard key={b.id} boa={b} />)}
          </div>
        )}
      </div>
    </div>
  );
}
